// MapView.jsx
import React, { useState, useCallback } from "react";
import { Map, Marker, NavigationControl } from "react-map-gl/mapbox";
import { Link } from "react-router-dom";
import slugify from "slugify";
import { accessToken, siteUrl } from "../../utils/constants";
import type { DocumentEntity } from "../../types/strapi";
import { mediaUrl } from "../../utils/imageUtils";

interface MapViewProps {
  items: DocumentEntity[];
  selectedMarker?: DocumentEntity | null;
  onMarkerSelect: (marker: DocumentEntity | null) => void;
  onItemClick: (item: DocumentEntity) => void;
  onKeyDown: (item: DocumentEntity, event: React.KeyboardEvent) => void;
}

const MapView: React.FC<MapViewProps> = ({
  items,
  selectedMarker,
  onMarkerSelect,
  onItemClick,
  onKeyDown,
}) => {
  const [viewport, setViewport] = useState({
    latitude: 0,
    longitude: 0,
    zoom: 1.6,
  });

  const handleMarkerClick = useCallback(
    (item: DocumentEntity) => {
      onMarkerSelect(item);
    },
    [onMarkerSelect]
  );

  return (
    <div
      className="mt-xl-5"
      style={{ width: "100%", height: "800px", border: "0.5px solid #ccc" }}
    >
      <Map
        latitude={viewport.latitude}
        longitude={viewport.longitude}
        zoom={viewport.zoom}
        mapboxAccessToken={accessToken}
        mapStyle="mapbox://styles/mapbox/outdoors-v11"
        onMove={(evt) => setViewport(evt.viewState)}
        scrollZoom={false}
        keyboard={false}
        doubleClickZoom={false}
        style={{ width: "100%", height: "100%" }}
      >
        <div style={{ position: "absolute", top: 10, left: 10 }}>
          <NavigationControl />
        </div>
        {items.map((item, index) => {
          const latitude = item?.contentType?.latitude;
          const longitude = item?.contentType?.longitude;

          if (latitude && longitude) {
            return (
              <Marker key={index} latitude={latitude} longitude={longitude}>
                <button
                  type="button"
                  onClick={() => handleMarkerClick(item)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-label="Select marker"
                >
                  <img
                    src="https://docs.mapbox.com/mapbox-gl-js/assets/custom_marker.png"
                    alt="marker"
                    style={{
                      width: "20px",
                      height: "20px",
                      filter:
                        selectedMarker?.id === item.id
                          ? "hue-rotate(180deg)"
                          : "none",
                    }}
                  />
                </button>
              </Marker>
            );
          }

          return null;
        })}
        {selectedMarker && (
          <MarkerDetail
            selectedMarker={selectedMarker}
            onClose={() => onMarkerSelect(null)}
            onItemClick={onItemClick}
            onKeyDown={onKeyDown}
          />
        )}
      </Map>
    </div>
  );
};

// MarkerDetail component (part of MapView)
interface MarkerDetailProps {
  selectedMarker: DocumentEntity;
  onClose: () => void;
  onItemClick: (item: DocumentEntity) => void;
  onKeyDown: (item: DocumentEntity, event: React.KeyboardEvent) => void;
}

const MarkerDetail: React.FC<MarkerDetailProps> = ({
  selectedMarker,
  onClose,
  onItemClick,
  onKeyDown,
}) => (
  <div className="side-window sidebar">
    <div className="p-4">
      <button type="button" onClick={onClose} className="close-button">
        &times;
      </button>
      <h4 className="mt-xl-5 mb-xl-4">
        <Link
          to={`/archive/${
            selectedMarker?.contentType?.title
              ? slugify(selectedMarker.contentType.title, {
                  lower: true,
                  strict: true,
                  trim: true,
                })
              : ""
          }`}
          className="no-hover"
          onClick={() => onItemClick(selectedMarker)}
          onKeyDown={(event) => onKeyDown(selectedMarker, event)}
        >
          {selectedMarker.contentType.title || ""}
        </Link>
      </h4>
      {selectedMarker.contentType.image?.url && (
        <div>
          <Link
            to={`/archive/${
              selectedMarker?.contentType?.title
                ? slugify(selectedMarker.contentType.title, {
                    lower: true,
                    strict: true,
                    trim: true,
                  })
                : ""
            }`}
            onClick={() => onItemClick(selectedMarker)}
            onKeyDown={(event) => onKeyDown(selectedMarker, event)}
          >
            <img
              src={mediaUrl(selectedMarker.contentType.image.url)}
              alt="Archive item"
              className="w-100"
            />
          </Link>
        </div>
      )}
      <div className="mt-xl-4">
        <p>{selectedMarker.contentType.description}</p>
        <p>
          <strong>Category:</strong>{" "}
          {
            selectedMarker.contentType.category?.name
          }
        </p>
        <p>
          <strong>Author:</strong>{" "}
          {selectedMarker.contentType.author}
        </p>
      </div>
    </div>
  </div>
);

export default React.memo(MapView);
